import { QuoQuestLevel, Reward } from "../types/quest";
import { generateTrendGraph } from "../utils/chartUtils";
import { randomizeIcon } from "../utils/botUtils";
import { globalWhitelist } from "../configs/whitelist";
import moment from "moment/moment";

// Please populate relevant bots for each stage along with the correct backtestConfig dates

const questRewards: Reward[] = [
  {
    message:
      "Skillful view allows you to dig deeper into the inner workings of your trading bot. Using this view allows users to edit their trading strategies in more detail.",
    name: "Skillful View",
    unlock: {
      availableIndicators: ["rsi", "profitMargin", "stopLoss"],
      editView: true,
      views: ["noob", "beginner"],
      level: 1
    }
  },
  {
    message:
      "Choose the base currency that your bot converts everything back into. Market presents different opportunities on different markets.",
    name: "Edit Quote Asset",
    unlock: {
      editQuoteAsset: true,
      quoteAssets: ["USDT", "BTC", "BNB"],
      level: 2
    }
  },
  {
    message: "Pick the coins you want to trade with",
    name: "Top 10 Coin Picker",
    unlock: {
      editWhitelist: true,
      whitelist: [
        "USDT",
        "BTC",
        "BNB",
        "ETH",
        "LUNA",
        "XRP",
        "SOL",
        "ADA",
        "AVAX",
        "DOT"
      ],
      level: 3
    }
  },
  {
    message:
      "Select the amount of trade slots that your bot will have available to fill. This determines the amount of your Quote asset that will be used for each trade",
    name: "Specify Trade Slots",
    unlock: {
      editTradeSlots: true,
      maxTradeSlots: 5,
      level: 4
    }
  },
  {
    message: "Decide what percentage of your portfolio to use for trading",
    name: "Specify Trade Ratio",
    unlock: {
      editTradeRatio: true,
      level: 5
    }
  },
  {
    message: "Edit compound trading. Compounding can yield higher returns",
    name: "Enable / Disable Compound Trading",
    unlock: {
      editCompound: true,
      level: 6
    }
  },
  {
    message: "Expert view allows you to configure the bot like a pro.",
    name: "Expert View",
    unlock: {
      views: ["noob", "beginner", "expert"],
      level: 7
    }
  },
  {
    message: "Change the intervals between readings for indicator evaluation",
    name: "Set Ticker Interval",
    unlock: {
      editTickerInterval: true,
      tickerIntervals: ["5m", "15m", "30m"],
      level: 8
    }
  },
  {
    message: "You can now also trade with DOGE and SHIB",
    name: "Additional Coins",
    unlock: {
      whitelist: [
        "USDT",
        "BTC",
        "BNB",
        "ETH",
        "LUNA",
        "XRP",
        "SOL",
        "ADA",
        "AVAX",
        "DOT",
        "DOGE",
        "SHIB"
      ],
      level: 9
    }
  },
  {
    message:
      "Set time to pause / stop trading on an asset that has recently made a loss.",
    name: "Enable / Disable Pause Trading",
    unlock: {
      editPauseDroppedCoins: true,
      level: 10
    }
  },
  {
    message:
      "Trade the high volumes. Allowed trading using volume totals on an asset over the last 24 hours",
    name: "Volume Indicator",
    unlock: {
      availableIndicators: ["rsi", "profitMargin", "stopLoss", "24HourVolume"],
      level: 11
    }
  },
  {
    message: "You can distribute your funds to holdings on multiple assets",
    name: "Holding Strategy",
    unlock: {
      editStrategyType: true,
      level: 12
    }
  },
  {
    message: "Trade on more coins simultaneously",
    name: "Additional Trade Slots",
    unlock: {
      maxTradeSlots: 10,
      level: 13
    }
  },
  {
    message: "You can now also trade with LTC, UNI and TRX",
    name: "Additional Coins",
    unlock: {
      whitelist: [
        "USDT",
        "BTC",
        "BNB",
        "ETH",
        "LUNA",
        "XRP",
        "SOL",
        "ADA",
        "AVAX",
        "DOT",
        "DOGE",
        "SHIB",
        "LTC",
        "UNI",
        "TRX"
      ],
      level: 14
    }
  },
  {
    message: "Unlock all supported coins and indicators",
    name: "All Coins and Indicators",
    unlock: {
      whitelist: globalWhitelist.map(item => item.toUpperCase()),
      level: 15
    }
  }
];

const genericBotData = {
  active: false,
  exchange: {
    bnbRatio: 0.03,
    name: "binance"
  },
  img: randomizeIcon(),
  lockSettings: true,
  mode: "backtest",
  notifications: false,
  orderType: "market",
  provisioned: false,
  user: "6qnaVBS64JdoP7q0ST9SHBkEHMH2",
  strategyType: "ta",
  quest: true // or questId
};

const genericTradeParameters = () => ({
  buy: {
    buyAmount: 0.005, // fixed
    compound: false, // fixed
    // vary between 1 - 5
    maxOpenTrades: Math.floor(Math.random() * 5) + 1,
    strategies: [
      {
        name: "rsi", // fixed
        operator: "and", // fixed
        // vary between 20 - 60 higher is more aggressive
        threshold: Math.floor(Math.random() * 40) + 20
      }
    ]
  },
  minimumTradePrice: 0.0005, // fixed
  minimumUnitPrice: 1e-8, // fixed
  quidproCommission: 0.00025, //fixed
  sell: {
    strategies: [
      {
        name: "profitMargin",
        operator: "or",
        // vary betweem 0.5 and 3
        threshold: Math.floor(Math.random() * 2.5) + 0.5
      },
      {
        name: "stopLoss",
        operator: "and",
        // vary between -1 and -5
        threshold: Math.floor(Math.random() * -4) - 1
      }
    ]
  },
  tickerInterval: "30m",
  // vary between 20 and 70
  tradeRatio: Math.floor(Math.random() * 50) + 20
});

type LevelData = {
  lockedUntil: Date | null;
  completionPercent: number;
};

const genericLevelData: LevelData = {
  lockedUntil: null,
  completionPercent: 0
};

const genericStageData = {
  attemptsLeft: 3,
  lockedUntil: null,
  hasCleared: false
};
// extract levelNames
const levelNames = [
  "Cryptocurrency Boom",
  "Bitcoin Cash Fork",
  "Crypto Market Crash",
  "Bitcoin Reaches Nearly $14k",
  "Bitcoin Halving 2020",
  "DeFi Summer",
  "Doge TikTok Rally",
  "Ethereum 2.0 Phase 0 Launched",
  "NFT Boom",
  "Cryptocurrency Market Crash",
  "Ethereum's London Hard Fork",
  "Crypto Market Crash",
  "LUNA Price Crash",
  "Ethereum Completes the Merge to Proof-of-Stake",
  "El Salvador Adopts Bitcoin as Legal Tender"
];

// extract from mockData
const levelStageDescriptions = [
  [
    "Trade BTC during the cryptocurrency boom.",
    "Expand your BTC portfolio amidst the boom."
  ],
  [
    "Trade BCH during the Bitcoin Cash fork.",
    "Forge ahead with Bitcoin Cash trading."
  ],
  [
    "Navigate through the market crash by trading conservatively.",
    "Master the art of conservative trading through market tumult."
  ],
  [
    "Trade BTC as it reaches nearly $14k.",
    "Harness BTC's momentum in your trading journey."
  ],
  [
    "Trade BTC during the Bitcoin halving.",
    "Advance your trading strategy amidst BTC halving."
  ],
  [
    "Trade ETH during the DeFi summer.",
    "Ride the DeFi wave with adept ETH trading."
  ],
  [
    "Trade DOGE during the TikTok rally.",
    "Capitalize on the DOGE TikTok rally with strategic trades."
  ],
  [
    "Trade ETH as Ethereum 2.0 Phase 0 is launched.",
    "Deepen your engagement with the dawn of Ethereum 2.0."
  ],
  [
    "Trade BTC during the NFT boom.",
    "Capitalize on the NFT surge with strategic BTC trades."
  ],
  [
    "Navigate through the market crash by trading conservatively.",
    "Continue mastering conservative trading amidst market adversities."
  ],
  [
    "Trade ETH during the London Hard Fork upgrade.",
    "Adapt to the London Hard Fork with refined ETH trading tactics."
  ],
  [
    "Navigate through the market crash by trading conservatively.",
    "Sustain your conservative trading prowess through market tribulations."
  ],
  [
    "Trade LUNA during its price crash, focusing on risk management.",
    "Evolve your risk management strategies with LUNA."
  ],
  [
    "Trade ETH as Ethereum completes the Merge to Proof-of-Stake.",
    "Navigate the PoS transition with astute ETH trading."
  ],
  [
    "Trade BTC during El Salvador's historic move to adopt Bitcoin as legal tender.",
    "Explore new horizons with BTC amidst El Salvador's bold move."
  ]
];

// extract from mockData
const marketTypes: ("Bull" | "Bear")[] = [
  "Bull",
  "Bull",
  "Bear",
  "Bull",
  "Bull",
  "Bull",
  "Bull",
  "Bull",
  "Bull",
  "Bear",
  "Bull",
  "Bear",
  "Bear",
  "Bear",
  "Bull"
];

// bots per level
const botNames = [
  ["BullionBot", "SurgeSentry"],
  ["ForkFinder", "SplitSpecter"],
  ["CrashCaptain", "DownturnDetective"],
  ["PeakPioneer", "RallyRanger"],
  ["HalveHarbinger", "RewardRover"],
  ["YieldYarn", "ProtocolPioneer"],
  ["Doge Dominator", "MemeKing"],
  ["StakerStealth", "BeaconBot"],
  ["TokenTitan", "NonFungibleNavigator"],
  ["CrashCaptain", "LossLocator"],
  ["ForkFortress", "UpgradeUsher"],
  ["CrashCommander", "BearBrigade"],
  ["LunaLander", "TerraTrooper"],
  ["MergeMajesty", "StakerStealth"],
  ["LegalTendertron", "BitBolivar"]
];

const botImages = botNames.map(botName => [randomizeIcon(), randomizeIcon()]);

// Please use dates for stages
const stagesTradingPeriods = [
  [
    { from: "2017-12-01", to: "2017-12-03" },
    { from: "2017-12-04", to: "2017-12-06" }
  ],
  [
    { from: "2017-08-01", to: "2017-08-03" },
    { from: "2017-08-04", to: "2017-08-06" }
  ],
  [
    { from: "2018-01-01", to: "2018-01-03" },
    { from: "2018-01-04", to: "2018-01-06" }
  ],
  [
    { from: "2019-06-01", to: "2019-06-03" },
    { from: "2019-06-04", to: "2019-06-06" }
  ],
  [
    { from: "2020-05-11", to: "2020-05-13" },
    { from: "2020-05-14", to: "2020-05-16" }
  ],
  [
    { from: "2020-06-01", to: "2020-06-03" },
    { from: "2020-06-04", to: "2020-06-06" }
  ],
  [
    { from: "2020-06-10", to: "2020-06-12" },
    { from: "2020-06-13", to: "2020-06-15" }
  ],
  [
    { from: "2020-12-01", to: "2020-12-03" },
    { from: "2020-12-04", to: "2020-12-06" }
  ],
  [
    { from: "2021-01-01", to: "2021-01-03" },
    { from: "2021-01-04", to: "2021-01-06" }
  ],
  [
    { from: "2021-05-01", to: "2021-05-03" },
    { from: "2021-05-04", to: "2021-05-06" }
  ],
  [
    { from: "2021-08-05", to: "2021-08-07" },
    { from: "2021-08-08", to: "2021-08-10" }
  ],
  [
    { from: "2022-01-01", to: "2022-01-03" },
    { from: "2022-01-04", to: "2022-01-06" }
  ],
  [
    { from: "2022-05-01", to: "2022-05-03" },
    { from: "2022-05-04", to: "2022-05-06" }
  ],
  [
    { from: "2022-09-01", to: "2022-09-03" },
    { from: "2022-09-04", to: "2022-09-06" }
  ],
  [
    { from: "2021-09-07", to: "2021-09-09" },
    { from: "2021-09-10", to: "2021-09-12" }
  ] // level 14
];

const botQuoteAssets = [
  ["USDT", "BTC"], // level 1: Cryptocurrency Boom, Ripple (XRP) was the best-performing coin, but BTC is more common as a quote asset.
  ["USDT", "BTC"], // level 2: Bitcoin Cash Fork, Bitcoin (BTC) was the best-performing coin.
  ["USDT", "BTC"], // level 3: Crypto Market Crash, Ethereum (ETH) was the best-performing coin, but BTC is more common as a quote asset.
  ["USDT", "BNB"], // level 4: Bitcoin Reaches Nearly $14k, Binance Coin (BNB) was the best-performing coin.
  ["USDT", "BTC"], // level 5: Bitcoin Halving 2020, Kusama (KSM) was the best-performing coin, but BTC is more common as a quote asset.
  ["USDT", "ETH"], // level 6: DeFi Summer, Kusama (KSM) was the best-performing coin, but DeFi is closely associated with Ethereum (ETH).
  ["USDT", "BNB"], // level 7: Doge TikTok Rally, Binance Coin (BNB) was the best-performing coin.
  ["USDT", "ETH"], // level 8: Ethereum 2.0 Phase 0 Launched, Kusama (KSM) was the best-performing coin, but this level is related to Ethereum (ETH).
  ["USDT", "BTC"], // level 9: NFT Boom, Decentraland (MANA) was the best-performing coin, but BTC is more common as a quote asset.
  ["USDT", "BTC"], // level 10: Cryptocurrency Market Crash, Decentraland (MANA) was the best-performing coin, but BTC is more common as a quote asset.
  ["USDT", "BTC"], // level 11: Ethereum's London Hard Fork, Decentraland (MANA) was the best-performing coin, but BTC is more common as a quote asset.
  ["USDT", "BTC"], // level 12: Crypto Market Crash, No information on best-performing coin, BTC is more common as a quote asset.
  ["USDT", "BTC"], // level 13: Terra-LUNA Crash, No information on best-performing coin, BTC is more common as a quote asset.
  ["USDT", "BTC"], // level 14: Ethereum Merge, No information on best-performing coin, BTC is more common as a quote asset.
  ["USDT", "BTC"] // level 15: El Salvador Bitcoin Adoption, Decentraland (MANA) was the best-performing coin, but BTC is more common as a quote asset.
];

const botWhitelists = [
  [
    ["BTC", "ETH"],
    ["XRP", "ETH"]
  ],
  [
    ["BTC", "ETH", "BCH"],
    ["XRP", "ETH", "BCH"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC"],
    ["XRP", "ETH", "BCH", "LTC"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB"],
    ["XRP", "ETH", "BCH", "LTC", "BNB"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "DOGE"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "DOGE"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "KSM", "MANA"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "ATOM", "MANA"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "", "MANA"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "SOL", "MANA"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "BTG", "MANA"]
  ],
  [
    ["BTC", "ETH", "BCH", "LTC", "BNB", "DOT", "SHIB"],
    ["XRP", "ETH", "BCH", "LTC", "BNB", "SOL", "MANA"]
  ]
];

// generate mock data from input data according to level structure as above
const levels: QuoQuestLevel[] = levelNames.map((name, index) => ({
  level: index + 1,
  levelName: name,
  marketDateFrom: new Date(stagesTradingPeriods[index][0].from),
  marketDateTo: new Date(stagesTradingPeriods[index][1].to),
  ...genericLevelData,
  status: index === 0 ? "unlocked" : "locked",
  marketType: marketTypes[index],
  reward: questRewards[index],
  stages: stagesTradingPeriods[index].map((stage, stageIndex) => ({
    stageId: stageIndex + 1,
    marketDateFrom: new Date(stage.from),
    marketDateTo: new Date(stage.to),
    marketType: marketTypes[index],
    featureUnlock: questRewards[index].name,
    description: levelStageDescriptions[index][stageIndex],
    // trend graph should start 4 days before stage.from
    graph: generateTrendGraph(
      moment(stage.from).subtract(4, "days").format("YYYY-MM-DD"),
      10,
      marketTypes[index] === "Bull" ? "rising" : "falling"
    ),
    ...genericStageData,
    bots: botNames[index].map((botName, nameIndex) => ({
      ...genericBotData,
      name: botName,
      img: botImages[index][nameIndex],
      backtestConfig: {
        endTime: stage.to + " 23:59",
        startTime: stage.from + " 00:00"
      },
      quoteAsset: botQuoteAssets[index][nameIndex],
      whitelist: botWhitelists[index][nameIndex],
      tradeParameters: genericTradeParameters()
    }))
  }))
}));

export const mockLevels = levels;
